<template>
  <v-container fluid>
    <h2 class="page-title mb-4">Ranking de planes</h2>

    <!-- Resumen: precio promedio y moda -->
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="Precio promedio"
          :firstValue="averagePrimaryLabel"
          :secondValue="averageSecondaryLabel"
          :showCsvButton="false"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="Precio moda"
          :firstValue="modePrimaryLabel"
          :secondValue="modeSecondaryLabel"
          :showCsvButton="false"
        />
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row class="mt-2" align="center">
      <v-col cols="12" sm="6" md="4">
        <v-select
          v-model="selectedCountryId"
          :items="countries"
          item-text="text"
          item-value="id"
          :loading="loadingCountries"
          label="País"
          placeholder="Selecciona un país"
          dense
          hide-details
          outlined
          @change="loadRanking"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="5">
        <v-select
          v-model="selectedLineas"
          :items="lineaOptions"
          label="Línea de negocio"
          multiple
          chips
          small-chips
          dense
          hide-details
          outlined
          @change="onLineasChange"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loadingData"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 50, 100] }"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="elevation-1 clickable-rows"
          @click:row="openServicesDialog"
        >
          <template v-slot:item.price="{ item }">
            {{ formatPrice(item.price, item.currency) }}
          </template>
          <template v-slot:item.activeMembershipsCount="{ item }">
            <v-chip x-small color="primary" dark>{{ item.activeMembershipsCount }}</v-chip>
          </template>
          <template v-slot:item.planName="{ item }">
            <a class="plan-link" @click.stop="openServicesDialog(item)">{{ item.planName }}</a>
          </template>
          <template v-slot:no-data>
            <div class="py-4">
              {{ selectedCountryId ? 'Sin resultados para los filtros seleccionados.' : 'Selecciona un país para ver el ranking.' }}
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Modal de servicios del plan -->
    <v-dialog v-model="showServicesDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">
          Servicios de {{ selectedPlanName }}
        </v-card-title>
        <v-card-text>
          <div v-if="loadingServices" class="text-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="planServices.length === 0" class="py-4 text--secondary">
            Este plan no tiene servicios asociados.
          </div>
          <v-list v-else dense>
            <v-list-item v-for="(sp, idx) in planServices" :key="idx">
              <v-list-item-icon>
                <v-icon color="primary">mdi-check-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ serviceTitle(sp) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="showServicesDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import CardResumeCompanyDetails from '../components/CardResumeCompanyDetails';
import PlanRankingService from '../services/plan-ranking.service';
import PlansService from '../services/plans.service';
import CountryService from '../services/countries.service';
import TipoCambioService from '../services/tipo-cambio.service';

const BUSINESS_LINES = ['B2B Employee Benefit', 'B2B Asistencias'];

export default {
  name: 'PlanRanking',
  mixins: [utilsMixin],
  components: { CardResumeCompanyDetails },
  data() {
    return {
      countries: [],
      loadingCountries: false,
      selectedCountryId: null,
      lineaOptions: BUSINESS_LINES,
      selectedLineas: ['B2B Employee Benefit'],
      rows: [],
      summary: { averagePrice: 0, modePrice: null, currency: null, rowsCount: 0 },
      // Mapa moneda → unidades por 1 USD (USD = monto / tasa)
      exchangeRates: {},
      loadingData: false,
      sortBy: 'activeMembershipsCount',
      sortDesc: true,
      headers: [
        { text: 'Plan', value: 'planName' },
        { text: 'Empresa', value: 'companyName' },
        { text: 'ID contrato', value: 'contratoCodigo' },
        { text: 'Línea de negocio', value: 'lineaNegocio' },
        { text: 'Precio', value: 'price' },
        { text: '# Membresías activas', value: 'activeMembershipsCount' }
      ],
      // Modal
      showServicesDialog: false,
      loadingServices: false,
      selectedPlanName: '',
      planServices: []
    };
  },
  computed: {
    rowCurrencies() {
      return [...new Set(this.rows.map(r => r.currency).filter(Boolean))];
    },
    isMixedCurrency() {
      return this.rowCurrencies.length > 1;
    },
    baseCurrency() {
      const country = this.countries.find(c => c.id === this.selectedCountryId);
      return (country && country.currency) ? country.currency : 'CLP';
    },
    averageParts() {
      return this.buildSummaryParts('avg');
    },
    modeParts() {
      return this.buildSummaryParts('mode');
    },
    averagePrimaryLabel() {
      return this.primaryLabel(this.averageParts);
    },
    averageSecondaryLabel() {
      return this.secondaryLabel(this.averageParts);
    },
    modePrimaryLabel() {
      return this.primaryLabel(this.modeParts);
    },
    modeSecondaryLabel() {
      return this.secondaryLabel(this.modeParts);
    }
  },
  mounted() {
    this.loadCountries();
    this.loadExchangeRates();
  },
  methods: {
    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        if (res && res.status === 'ok') {
          this.countries = res.data.map(c => ({
            text: c.name ?? '',
            id: c.id,
            currency: c.currency || 'CLP'
          }));
          // Por defecto: Chile preseleccionado y carga inicial del ranking
          const chile = this.countries.find(c => (c.text || '').toLowerCase() === 'chile');
          if (chile && !this.selectedCountryId) {
            this.selectedCountryId = chile.id;
            this.loadRanking();
          }
        }
      } catch {
        this.showErrorSnackbar('No se pudieron cargar los países');
      }
      this.loadingCountries = false;
    },

    async loadExchangeRates() {
      try {
        const res = await TipoCambioService.getAll();
        if (res && res.status === 'ok' && Array.isArray(res.data)) {
          this.exchangeRates = res.data.reduce((acc, r) => {
            acc[r.currency] = Number(r.rate);
            return acc;
          }, {});
        }
      } catch {
        // silencioso — el aproximado en USD no es bloqueante
      }
    },

    onLineasChange() {
      if (this.selectedCountryId) {
        this.loadRanking();
      }
    },

    async loadRanking() {
      if (!this.selectedCountryId) return;
      if (this.selectedLineas.length === 0) {
        this.rows = [];
        this.summary = { averagePrice: 0, modePrice: null, currency: null, rowsCount: 0 };
        return;
      }
      this.loadingData = true;
      try {
        const res = await PlanRankingService.getPlanRanking(
          this.selectedCountryId,
          this.selectedLineas
        );
        if (res.status === 'ok') {
          this.rows = res.data.rows;
          this.summary = res.data.summary;
        } else {
          this.showErrorSnackbar('No se pudo cargar el ranking de planes');
        }
      } catch {
        this.showErrorSnackbar('No se pudo cargar el ranking de planes');
      }
      this.loadingData = false;
    },

    async openServicesDialog(item) {
      this.selectedPlanName = item.planName;
      this.planServices = [];
      this.showServicesDialog = true;
      this.loadingServices = true;
      try {
        const res = await PlansService.getPlanDetails(item.planId);
        if (res.status === 'ok') {
          this.planServices = res.data.servicePlanDetails ?? [];
        } else {
          this.showErrorSnackbar('No se pudieron cargar los servicios del plan');
        }
      } catch {
        this.showErrorSnackbar('No se pudieron cargar los servicios del plan');
      }
      this.loadingServices = false;
    },

    serviceTitle(servicePlan) {
      if (servicePlan && servicePlan.service && servicePlan.service.title) {
        return servicePlan.service.title;
      }
      return 'Servicio';
    },

    // Calcula el resumen (promedio o moda) normalizado a USD y su referencia en la
    // moneda base del país. `kind` = 'avg' | 'mode'.
    // - Homogéneo (todas las filas misma moneda): se calcula en la moneda local y se
    //   expresa en USD; si la moneda local coincide con la base, la referencia es exacta.
    // - Mixto (ej. UF + CLP): se calcula en USD y la referencia base es la conversión USD→base.
    buildSummaryParts(kind) {
      if (this.rows.length === 0) return { usd: null, base: null, exact: false };
      const base = this.baseCurrency;
      const rateBase = this.exchangeRates[base];

      if (this.isMixedCurrency) {
        let usdValues = this.rows
          .map(r => this.priceToUsd(r.price, r.currency))
          .filter(v => v !== null);
        if (kind === 'mode') {
          usdValues = usdValues.map(v => Math.round(v * 100) / 100);
        }
        if (usdValues.length === 0) return { usd: null, base: null, exact: false };
        const usd = kind === 'avg' ? this.average(usdValues) : this.mode(usdValues);
        const baseVal = (rateBase && rateBase > 0) ? usd * rateBase : null;
        return { usd, base: baseVal, exact: false };
      }

      const localCurrency = this.rowCurrencies[0];
      const localValues = this.rows.map(r => r.price);
      const local = kind === 'avg' ? this.average(localValues) : this.mode(localValues);
      const usd = this.priceToUsd(local, localCurrency);
      if (localCurrency === base) {
        return { usd, base: local, exact: true };
      }
      const baseVal = (usd !== null && rateBase && rateBase > 0) ? usd * rateBase : null;
      return { usd, base: baseVal, exact: false };
    },

    primaryLabel(parts) {
      if (!parts || parts.usd === null || parts.usd === undefined) {
        if (parts && parts.base !== null) return this.formatPrice(parts.base, this.baseCurrency);
        return '—';
      }
      return '≈ US$' + this.formatUsdNumber(parts.usd);
    },

    secondaryLabel(parts) {
      // Si no hay valor principal en USD, ya se mostró la referencia como principal.
      if (!parts || parts.usd === null || parts.usd === undefined) return '';
      if (parts.base === null) return '';
      const prefix = parts.exact ? '' : '≈ ';
      return prefix + this.formatPrice(parts.base, this.baseCurrency);
    },

    average(values) {
      if (!values.length) return 0;
      const total = values.reduce((acc, v) => acc + Number(v), 0);
      return Math.round((total / values.length) * 100) / 100;
    },

    mode(values) {
      if (!values.length) return null;
      const frequency = new Map();
      for (const v of values) {
        frequency.set(v, (frequency.get(v) ?? 0) + 1);
      }
      let modeVal = values[0];
      let maxFrequency = 0;
      for (const [val, freq] of frequency.entries()) {
        if (freq > maxFrequency) {
          maxFrequency = freq;
          modeVal = val;
        } else if (freq === maxFrequency && val < modeVal) {
          modeVal = val;
        }
      }
      return modeVal;
    },

    priceToUsd(value, currency) {
      if (value === null || value === undefined) return null;
      const cur = currency || 'CLP';
      if (cur === 'USD') return Number(value);
      const rate = this.exchangeRates[cur];
      if (!rate || rate <= 0) return null;
      return Number(value) / rate;
    },

    formatUsdNumber(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    },

    formatPrice(value, currency) {
      const amount = Number(value) || 0;
      const cur = currency || 'CLP';
      try {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: cur,
          maximumFractionDigits: 0
        }).format(amount);
      } catch {
        return `${amount} ${cur}`;
      }
    }
  }
};
</script>

<style scoped>
.page-title {
  color: #104b94;
  font-weight: 700;
}
.plan-link {
  color: #104b94;
  font-weight: 600;
  cursor: pointer;
}
.clickable-rows >>> tbody tr {
  cursor: pointer;
}
</style>
